import { Box, Divider, Paper, Stack, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconLeafWriter } from '../../icons';
import { useActions, useAppState } from '../../overmind';
import { EntityType } from '../../types';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Toggle } from './Toggle';

type ItemType = 'button' | 'divider' | 'iconButton' | 'toggle';
type ItemGroup = 'action' | 'ui' | 'panel' | 'general';

export interface Item {
  disabled?: boolean;
  hide?: boolean;
  type: ItemType;
  group: ItemGroup;
}

export interface MenuItem extends Item {
  color?: string;
  group: ItemGroup;
  icon: IconLeafWriter;
  onClick?: () => void;
  selected?: boolean;
  title?: string;
  tooltip?: string;
}

export const EditorToolbar = () => {
  const { t } = useTranslation();
  const { schemaId } = useAppState().document;
  const { isReadonly, showTags } = useAppState().editor;
  const { fullscreen } = useAppState().ui;

  const { toggleShowTags } = useActions().editor;
  const { openDialog, showContextMenu, toggleFullscreen } = useActions().ui;

  const { entity, spacing } = useTheme();

  const container = useRef<HTMLDivElement>(null);

  const isSupported = useCallback(
    (name: EntityType) => window.writer.schemaManager.mapper.getEntitiesMapping().has(name),
    [schemaId],
  );

  const items: (MenuItem | Item)[] = [
    {
      group: 'action',
      hide: isReadonly,
      icon: 'insertTag',
      onClick: () => {
        if (!container.current) return;

        const rect = container.current.getBoundingClientRect();
        const posX = rect.left;
        const posY = rect.top + 34;

        showContextMenu({
          // anchorEl: container.current,
          eventSource: 'ribbon',
          position: { posX, posY },
          useSelection: true,
        });
      },
      title: t('LW.editorToolbar.Tag'),
      tooltip: t('LW.editorToolbar.Add Tag'),
      type: 'button',
    },
    { group: 'action', type: 'divider', hide: isReadonly },
    {
      color: entity.person.color.main,
      group: 'action',
      disabled: !isSupported('person'),
      hide: isReadonly,
      icon: entity.person.icon,
      onClick: () => window.writer.tagger.addEntityDialog('person'),
      title: t('LW.editorToolbar.Tag Person'),
      type: 'iconButton',
    },
    {
      color: entity.place.color.main,
      group: 'action',
      disabled: !isSupported('place'),
      hide: isReadonly,
      icon: entity.place.icon,
      onClick: () => window.writer.tagger.addEntityDialog('place'),
      title: t('LW.editorToolbar.Tag Place'),
      type: 'iconButton',
    },
    {
      color: entity.organization.color.main,
      group: 'action',
      disabled: !isSupported('organization'),
      hide: isReadonly,
      icon: entity.organization.icon,
      onClick: () => window.writer.tagger.addEntityDialog('organization'),
      title: t('LW.editorToolbar.Tag Organization'),
      type: 'iconButton',
    },
    {
      color: entity.work.color.main,
      group: 'action',
      disabled: !isSupported('work'),
      hide: isReadonly,
      icon: entity.work.icon,
      onClick: () => window.writer.tagger.addEntityDialog('work'),
      title: t('LW.editorToolbar.Tag Work'),
      type: 'iconButton',
    },
    {
      color: entity.thing.color.main,
      group: 'action',
      disabled: !isSupported('thing'),
      hide: isReadonly,
      icon: entity.thing.icon,
      onClick: () => window.writer.tagger.addEntityDialog('thing'),
      title: t('LW.editorToolbar.Tag Thing'),
      type: 'iconButton',
    },
    {
      color: entity.citation.color.main,
      group: 'action',
      disabled: !isSupported('citation'),
      hide: isReadonly,
      icon: entity.citation.icon,
      onClick: () => window.writer.tagger.addEntityDialog('citation'),
      title: t('LW.editorToolbar.Tag Citation'),
      type: 'iconButton',
    },
    {
      color: entity.note.color.main,
      group: 'action',
      disabled: !isSupported('note'),
      hide: isReadonly,
      icon: entity.note.icon,
      onClick: () => window.writer.tagger.addEntityDialog('note'),
      title: t('LW.editorToolbar.Tag Note'),
      type: 'iconButton',
    },
    {
      color: entity.date.color.main,
      group: 'action',
      disabled: !isSupported('date'),
      hide: isReadonly,
      icon: entity.date.icon,
      onClick: () => window.writer.tagger.addEntityDialog('date'),
      title: t('LW.editorToolbar.Tag Date'),
      type: 'iconButton',
    },
    {
      color: entity.correction.color.main,
      group: 'action',
      disabled: !isSupported('correction'),
      hide: isReadonly,
      icon: entity.correction.icon,
      onClick: () => window.writer.tagger.addEntityDialog('correction'),
      title: t('LW.editorToolbar.Tag Correction'),
      type: 'iconButton',
    },
    {
      color: entity.keyword.color.main,
      group: 'action',
      disabled: !isSupported('keyword'),
      hide: isReadonly,
      icon: entity.keyword.icon,
      onClick: () => window.writer.tagger.addEntityDialog('keyword'),
      title: t('LW.editorToolbar.Tag Keyword'),
      type: 'iconButton',
    },
    {
      color: entity.link.color.main,
      group: 'action',
      disabled: !isSupported('link'),
      hide: isReadonly,
      icon: entity.link.icon,
      onClick: () => window.writer.tagger.addEntityDialog('link'),
      title: t('LW.editorToolbar.Tag Link'),
      type: 'iconButton',
    },
    { group: 'action', type: 'divider', hide: isReadonly },
    {
      icon: 'translate',
      group: 'action',
      hide: isReadonly,
      onClick: () => window.writer.dialogManager.show('translation'),
      title: t('LW.editorToolbar.Add Translation'),
      type: 'iconButton',
    },
    { group: 'action', type: 'divider', hide: isReadonly },
    {
      group: 'ui',
      icon: showTags ? 'showTagsOn' : 'showTagsOff',
      onClick: () => toggleShowTags(),
      selected: showTags,
      title: showTags ? t('LW.editorToolbar.Hide Tags') : t('LW.editorToolbar.Show Tags'),
      type: 'toggle',
    },
    {
      group: 'ui',
      icon: fullscreen ? 'fullscreenExit' : 'fullscreen',
      onClick: () => toggleFullscreen(),
      selected: fullscreen,
      title: t('LW.editorToolbar.Toggle Fullscreen'),
      type: 'toggle',
    },
    { group: 'ui', type: 'divider', hide: isReadonly },
    {
      group: 'ui',
      hide: isReadonly,
      icon: 'validate',
      onClick: () => {
        window.writer.layoutManager.showModule('validation');
        window.writer.validate();
      },
      title: t('LW.editorToolbar.Validate'),
      type: 'iconButton',
    },
    { group: 'ui', type: 'divider', hide: isReadonly },
    {
      group: 'ui',
      icon: 'settings',
      onClick: () => openDialog({ type: 'settings' }),
      title: t('LW.editorToolbar.Settings'),
      type: 'iconButton',
    },
    {
      group: 'ui',
      icon: 'documentation',
      onClick: () => {
        window.open('https://www.leaf-vre.org/docs/documentation/leaf-writer-documentation');
      },
      title: t('LW.editorToolbar.Documentation'),
      type: 'iconButton',
    },
  ];

  const ItemComponent = (item: MenuItem | Item) => {
    const BUTTON_TYPES: Record<ItemType, React.ReactNode> = {
      button: <Button {...(item as MenuItem)} />,
      iconButton: <IconButton {...(item as MenuItem)} />,
      toggle: <Toggle {...(item as MenuItem)} />,
      divider: (
        <Divider orientation="vertical" variant="middle" flexItem sx={{ width: spacing(2) }} />
      ),
    };
    return <>{BUTTON_TYPES[item.type]}</>;
  };

  const groups: ItemGroup[] = ['action', 'ui'];

  return (
    <Paper
      ref={container}
      elevation={5}
      square
      sx={[
        {
          width: '100%',
          backgroundColor: '#f5f5f5',
        },
        (theme) =>
          theme.applyStyles('dark', {
            backgroundColor: theme.vars.palette.background.paper,
          }),
      ]}
      component={motion.div}
      layout="size"
    >
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={0.25}
        px={0.5}
        py={0.25}
        component={motion.div}
        layout
        justifyContent="space-between"
      >
        <AnimatePresence mode="popLayout">
          {groups.map((group) => (
            <Stack key={group} direction="row" flexWrap="wrap">
              {items
                .filter((item) => !item.hide)
                .filter((item) => item.group === group)
                .map((item, index) => (
                  <Box
                    layout
                    key={'title' in item ? item.title : index}
                    component={motion.div}
                    initial={{ scale: 0, opacity: 0 }}
                    exit={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    <ItemComponent {...item} />
                  </Box>
                ))}
            </Stack>
          ))}
        </AnimatePresence>
      </Stack>
    </Paper>
  );
};
